//=============================================
// ARCHELIOS MAP NAMESPACE
//=============================================
var am = (function(module) {

    //Use strict mode
    'use strict';

    /**
     * @class am.PanelView
     * This class implements a responsive side panel, diplayed on the right or left side of the screen in landscape 
     * mode, and on the top or bottom side of the screen in portrait mode.
     */
    module.PanelView = L.Control.extend({
        // Public properties
        options: {
            anchor: 'lower-left',
            autoPan: true,
            customCssClass: "",
        },

        /**
         * Class constructor
         * @constructor
         *
         * @param {Object} options - Same options as for class L.Control, in addition to the following options:
         *      - "anchor": defines the position of the panel relative to the map container. This option can be either 
         *          "lower-left", "lower-right", "upper-left" or "upper-right". A panel placed on the "lower" side of the 
         *          map will be displayed on the bottom of the map on portrait screens (smartphones). Similarly, a panel 
         *          placed on the "upper" side of the map will be displayed to the top of the map on portrait screens. 
         *          Defaults to "lower-left".
         *      - "autoPan": specifies whether the map should be paned when the side panel appears or disappears (this
         *          gives a nice slide effect). Defaults to true.
         *      - "customCssClass": class(es) to be added to the panel container
         */
        initialize: function(options) {
            // Call parent constructor
            L.Control.prototype.initialize.call(this, options);

            // Create the DOM element which will contain the panel
            this._container = L.DomUtil.create('div', 'leaflet-panel leaflet-panel-' + this.options.anchor + ' ' + this.options.customCssClass); // hack: _container is a private propery of class L.Control
        },

        /**
         * Adds the panel to the map.
         *
         * @param {L.Map} map - Leaflet map in which the panel should be added
         * @return {am.PanelView} - Self-reference to the panel (allows for chained calls to methods)
         */
        addTo: function(map) {
            this.remove();

            // Add the DOM element representing the panel to the DOM tree
            map._controlContainer.appendChild(this._container);

            // Store reference to the map in the current object
            this._map = map;

            // Make sure we don't drag the map when we interact with the contents
            am.utils.preventMapInteractionOverElement(this._container);

            return this;
        },

        /**
         * Removes the panel from the map it belongs to.
         *
         * @return {am.PanelView} - Self-reference to the panel (allows for chained calls to methods)
         */
        remove: function() {
            // If the control is visible, hide it before removing it
            this.hide();

            // Remove panel container from the map's panel container
            if (this._map) {
                this._container.parentNode.removeChild(this._container);
                this._map = null;            
            }

            // Unregister events to prevent memory leak
            am.utils.restoreMapInteractionOverElement(this._container);

            return this;
        },

        /**
         * Returns the outer-most HTML element belonging to the panel
         *
         * @return {DOMElement} - Panel container
         */
        getContainer: function() {
            return this._container;
        },

        /**
         * Returns true if the panel is currently visible
         *
         * @return {Boolean} - True if the panel is visible, false otherwise
         */
        isVisible: function() {
            return L.DomUtil.hasClass(this._container, 'leaflet-panel-visible');
        },

        /**
         * Displays the panel (i.e. makes it visible). This method has an effect only if the panel was previously added
         * to a map.
         *
         * @return {am.PanelView} - Self-reference to the panel (allows for chained calls to methods)
         */
        show: function() {
            if (this._map && !this.isVisible()) { // hack: check that the panel has been added to a map before showing it
                L.DomUtil.addClass(this._container, 'leaflet-panel-visible');
                if (this.options.autoPan) {
                    this._panMap(true);
                }
            }

            return this;
        },

        /**
         * Hides the panel (i.e. makes it invisible). This method has an effect only if the panel was previously added
         * to a map.
         *
         * @return {am.PanelView} - Self-reference to the panel (allows for chained calls to methods)
         */
        hide: function() {
            if (this._map && this.isVisible()) {
                L.DomUtil.removeClass(this._container, 'leaflet-panel-visible');
                if (this.options.autoPan) {
                    this._panMap(false);
                }
            }

            return this;
        },

        /**
         * Displays the panel if it was hidden, or hide it if it was visible. This method has an effect only if the 
         * panel was previously added to a map.
         *
         * @return {am.PanelView} - Self-reference to the panel (allows for chained calls to methods)
         */
        toggle: function() {
            if (this.isVisible()) {
                this.hide();
            } else {
                this.show();
            }

            return this;
        },

        /**
         * Sets the HTML contents of the panel. Any previous content is deleted.
         *
         * @param {String|DomElement} contents - Contents to be inserted inside the panel.
         * @return {am.PanelView} - Self-reference to the panel (allows for chained calls to methods)
         */
        setContents: function(contents) {
            if (typeof contents === 'string') {
                this._container.innerHTML = contents;
            } else { // contents is a DOM element
                this._container.innerHTML = ""; // clean container contents
                this._container.appendChild(contents);
            }

            return this;
        },

        /**
         * Returns the map space which is occupied by the panel when it is visible
         *
         * @return {Array} - Map space occupied by the panel when visible. The returned array contains the map margins, 
         *      in pixels, which will be occupied by the panel. The first element of the array corresponds to the top
         *      margin of the map, the second element to the right margin, the third element to the bottom margin and 
         *      the last element to the left margin. 
         */
        reservedMapSpace: function() {
            var mapMargins = undefined;

            if (window.matchMedia("all and (orientation: landscape)").matches) { // landscape orientation
                mapMargins = (this.options.anchor.indexOf('left') != -1) ? 
                    [0, 0, 0, this._container.offsetWidth] :
                    [0, this._container.offsetWidth, 0, 0];
            } else { // portrait orientation
                mapMargins = (this.options.anchor.indexOf('lower') != -1) ? 
                    [0, 0, this._container.offsetHeight, 0] :
                    [this._container.offsetHeight, 0, 0, 0];
            }

            return mapMargins;
        },

        /**
         * Returns the panel dimensions
         *
         * @return {Array} - Dimensions of the panel, returned as [width, height] in pixels
         */
        getDimensions: function() {
            return [this._container.offsetWidth, this._container.offsetHeight];
        },

        /**
         * Pans the map in response to the apparition/removal of the panel
         *
         * @param {Boolean} panelAppears - Pan map in response to the apparition of the panel
         */
        _panMap: function(panelAppears) {
            var panOptions = {
                duration: 0.5,
            };

            if (window.matchMedia("all and (orientation: landscape)").matches) { // landscape orientation
                var panTowardsEast = (this.options.anchor.indexOf('left') != -1 && panelAppears) || (this.options.anchor.indexOf('left') == -1 && !panelAppears);
                var panDirection = panTowardsEast ? -1 : 1;
                this._map.panBy([panDirection*0.5*this._container.offsetWidth, 0], panOptions);
            } else { // portrait orientation
                var panTowardsNorth = (this.options.anchor.indexOf('lower') != -1 && panelAppears) || (this.options.anchor.indexOf('lower') == -1 && !panelAppears);
                var panDirection = panTowardsNorth ? -1 : 1;
                this._map.panBy([0, -panDirection*0.5*this._container.offsetHeight], panOptions);
            }
        },
    });

    return module;

})(am || {});


//=============================================
// AM.UTILS NAMESPACE
//=============================================
am.utils = (function(module) {

    // Use strict mode
    'use strict';

    /**
     * Factory function returning an instance of am.PanelView
     * 
     * @param {Object} options - Same options as for class L.Control
     * @return {am.PanelView} - New instance of class am.PanelView
     */
    module.panelView = function(options) {
        return new am.PanelView(options);
    };

    return module;

})(am.utils || {});